// HT-2 · 現在使用中の読み取り構成（表示のみ・折りたたみ）
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LEARN, authHeader, detailOf, Collapse } from './shared';

const Row = ({ label, value, testId }) => (
  <div className="flex flex-wrap gap-x-2 py-0.5" data-testid={testId}>
    <dt className="text-[11.5px] font-medium text-zinc-600">{label}</dt>
    <dd className="text-[11.5px] text-zinc-800">{value}</dd>
  </div>
);

export default function ReadingConfig() {
  const [cfg, setCfg] = useState(null);
  const [diag, setDiag] = useState(null);

  const load = useCallback(async (withDiagnostics) => {
    try {
      const r = await axios.get(`${LEARN}/reading-config`, {
        params: withDiagnostics ? { diagnostics: true } : {},
        headers: authHeader(),
      });
      setCfg(r.data);
      if (withDiagnostics) setDiag(r.data.diagnostics || null);
    } catch (e) { toast.error(detailOf(e, '読み取り構成を取得できませんでした')); }
  }, []);
  useEffect(() => { load(false); }, [load]);

  if (!cfg) return null;
  return (
    <Collapse label="現在使用中の読み取り構成" testId="reading-config">
      <dl className="space-y-0.5">
        <Row label="読み取りに使用中のモデル" value={cfg.model_label_ja}
          testId="reading-config-model" />
        <Row label="モデルの追加設定" value={cfg.customization_label_ja}
          testId="reading-config-customization" />
        <Row label="有効な学習内容" value={cfg.structured_learning_ja}
          testId="reading-config-version" />
        <Row label="最後の検証" value={cfg.last_validation_ja}
          testId="reading-config-validation" />
        <Row label="以前の内容に戻せるか" value={cfg.rollback_label_ja}
          testId="reading-config-rollback" />
        <Row label="学習候補の状態" value={cfg.proposal_state_ja}
          testId="reading-config-proposals" />
      </dl>
      <p className="pt-2 text-[11px] leading-relaxed text-zinc-500"
        data-testid="reading-config-note">
        {cfg.customization_reason_ja}
      </p>
      <p className="text-[11px] leading-relaxed text-zinc-500">{cfg.ht2_notice_ja}</p>
      <div className="pt-2">
        <Collapse label="管理者向けの詳細（表示のみ）" testId="reading-config-diagnostics">
          {!diag ? (
            <button type="button" onClick={() => load(true)}
              className="min-h-[32px] text-[11.5px] text-teal-700 hover:underline"
              data-testid="reading-config-diagnostics-load">詳細を読み込む</button>
          ) : (
            <dl className="space-y-0.5">
              <Row label="モデル識別子" value={diag.model_identifier || '—'} />
              <Row label="接続方式" value={`${diag.provider_mode} / ${diag.base_url || '—'}`} />
              <Row label="テキストモデル" value={diag.text_model || '—'} />
              <Row label="画像モデル" value={diag.vision_model || '—'} />
              <Row label="書類メタデータの割り当て" value={diag.routing_document_metadata || '—'} />
              <Row label="OCRの割り当て" value={diag.routing_ocr_vision || '—'} />
              <Row label="カスタムプロンプト" value={(diag.custom_prompt_keys || []).join(', ') || 'なし'} />
              <Row label="少数例パック" value={(diag.few_shot_packs || []).join(', ') || 'なし'} />
              <Row label="アダプタ・チェックポイントの記録" value="なし（要確認）" />
              <Row label="確認できていない項目" value={(diag.unverified_ja || []).join(' / ')} />
              <Row label="設定の版" value={`${diag.settings_version} · ${diag.settings_updated_by || '—'}`} />
              <Row label="台帳" value={diag.register_document} />
              <Row label="注意" value={diag.read_only_ja} />
            </dl>
          )}
        </Collapse>
      </div>
    </Collapse>
  );
}
